import {
    TenantChargingMode,
    TenantInvoiceIssuer,
    TenantPaymentChannel,
    TenantPayments,
    TenantReservationFlow,
    TenantServiceConfig,
} from '../../api/account/accountApiTypes';
import type { ProductCategory } from '../../api/productCategory/productCategoryApiSlice';
import { liveUseCasesWhere, spaceOf } from '../../utils/useCases';

/**
 * Use cases whose rows the tariff editor understands — duration tiers with
 * weekday scope and time window. Venue and apartment rows (a nightly row;
 * weekday + weekend rows) are edited by the room editor instead — on the
 * object page, and on the product in the products section (#2923).
 *
 * It lives in this module rather than next to the section that first needed it
 * because three surfaces now answer questions with it — the pricing section,
 * the products section, and the product form's description defaults — and one
 * of those is a test. Importing it from a component drags the whole RTK Query /
 * Auth0 module graph into the suite, which is why the seeding rule had no test
 * until it moved here.
 */
export const TARIFF_USE_CASES = new Set(['sup', 'rental', 'storage']);

/**
 * Use cases that file their products under a PRODUCT CATEGORY (ADR-0026 §B).
 *
 * ── Why this is a second set and not a wider `TARIFF_USE_CASES` ────────────
 *
 * They answer different questions. `TARIFF_USE_CASES` answers "does the tariff
 * editor understand this use case's rows?", and three surfaces read it for
 * exactly that. This one answers "are this use case's products filed under
 * categories?". Since #3429 step 3 the station units are in both, which is
 * why they are not one set: widening the tariff set to make the category
 * assignment reachable would hand `apartment`/`venue` a tariff editor they are
 * explicitly meant not to have.
 *
 * ── The counterpart, which cannot be imported ─────────────────────────────
 *
 * `CATEGORY_AWARE_USE_CASES` in
 * `apps/end-user/src/modules/home/homeTiles.ts` is the same list, deciding
 * which home tiles split per category. Two workspaces, two deploys, no shared
 * import — so both derive it from the description's `productCategories`, name
 * each other, and `serviceConfigurationRules.test.ts` pins that that file still
 * derives it the same way.
 *
 * The drift this prevents is silent in the worst direction: a use case added
 * there and not here gets category tiles on the home screen with no admin
 * surface to fill the categories, which is the outage #2857 was raised for.
 *
 * Its members are the use cases with `productCategories` (`utils/useCases.ts`):
 * the space template and, since #3429 step 3, the station units.
 */
export const CATEGORY_USE_CASES: ReadonlySet<string> = new Set<string>(
    liveUseCasesWhere((d) => d.productCategories),
);

/**
 * Use cases whose Group cell IS the object the customer picks — a room, an
 * apartment. A product of one of these is a TYPE its objects are assigned to
 * ("1-Zimmer/Küche/Bad", #2923), so:
 *
 *  - it is CREATED in the products section like any other product (since
 *    #2923), and its prices are set there too, in the room price dialog —
 *    the tariff editor does not speak room rows;
 *  - it is never RETIRED there: archiving an object-minted room product would
 *    leave a live room without one, dropping its price back to the legacy
 *    Group source and silently losing its category;
 *  - its edit never renames its objects — each keeps its own name,
 *    description and photos.
 *
 * Mirror of `ROOM_USE_CASES` in
 * `new-backend/admin/src/lib/product/productFields.ts` — two deploys, no
 * shared import, so they name each other. Same members as
 * `CATEGORY_USE_CASES` today, and deliberately a separate set: that one
 * answers "are products filed under categories?", this one "is the cell the
 * object?". Its members are the space template (`utils/useCases.ts`).
 */
export const ROOM_PRODUCT_USE_CASES: ReadonlySet<string> = new Set<string>(
    liveUseCasesWhere((d) => d.template === 'space'),
);

/**
 * Does this product belong to one of the section's use cases? Compared
 * case-insensitively, per the API-values rule.
 *
 * Reads `useCase` AND `compartmentTypes`, and that is not belt-and-braces. A
 * product created through the Services form always carries
 * `compartment_types: [useCase]`. A ROOM product does not: it is mirrored from
 * its Group by `ensureProductForGroup` and inherits whatever that Group has —
 * and `Group.compartment_types` is `TEXT[]` with no NOT NULL and no DEFAULT,
 * so it can legitimately be empty. The availability query reads an empty one as
 * "no use-case restriction" and offers the object anyway, so such a room is
 * live for customers while matching nothing here.
 *
 * Testing `compartmentTypes` alone would therefore have hidden exactly the
 * products #2857 exists to surface, on exactly the tenants it exists for — and
 * the section would have rendered an empty list reading as "no products yet"
 * rather than as a filter that missed.
 *
 * Structural parameter rather than `AdminProduct`: importing that type's module
 * into this one would drag the RTK Query / Auth0 graph into the suite, which is
 * the same reason the sets above live here.
 */
export const productInSectionFamily = (
    product: { useCase?: string | null, compartmentTypes?: string[] | null },
    sectionUseCases: string[],
): boolean => {
    const matches = (value: string | null | undefined): boolean =>
        !!value
        && sectionUseCases.some((service) => service.toLowerCase() === value.toLowerCase());
    return matches(product.useCase) || (product.compartmentTypes ?? []).some(matches);
};

/** Is this a room product (see `ROOM_PRODUCT_USE_CASES`)? */
export const isRoomProduct = (
    product: { useCase?: string | null, compartmentTypes?: string[] | null },
): boolean => productInSectionFamily(product, Array.from(ROOM_PRODUCT_USE_CASES));

/**
 * Use cases whose products are LISTED — and, since #2923, CREATED — in the
 * products section: all of them. Retiring stays with the tariff family (see
 * `ROOM_PRODUCT_USE_CASES`). Backend mirror, which refuses anything else on
 * create: `new-backend/admin/src/lib/product/productFields.FORM_USE_CASES`.
 */
export const LISTED_PRODUCT_USE_CASES = new Set(
    // `Array.from` rather than spreading the Sets: this workspace's production
    // build targets below es2015, where a Set spread needs `downlevelIteration`
    // and fails the build — while Jest transpiles it and stays green.
    Array.from(TARIFF_USE_CASES).concat(Array.from(CATEGORY_USE_CASES)),
);

/**
 * The rules of the service configuration — the closed answer sets, and what
 * each answer needs deposited in Account before it can be chosen.
 *
 * Mirror of `new-backend/admin/src/lib/serviceConfiguration.ts`, which rejects
 * anything else. The two cannot import each other (separate deploys), so they
 * name each other and are changed together. The backend copy is the one pinned
 * by tests (`serviceConfiguration.test.ts`); a divergence here shows up as a
 * form offering an option the endpoint refuses.
 *
 * See `docs/plans/service-configuration-area.md` §2, ADR-0016 Amendment 4
 * (the three questions) and Amendment 6 (channels are decided by the flow).
 */

/** The timing choice on the reservation path. `book_now` is deliberately
 *  absent: it is a capability switched on top of these, not a third value. */
export const RESERVATION_FLOWS: TenantReservationFlow[] = [
    'reserve_pay_first',
    'reserve_accept_postpay',
];

/** Channels a reservation can be settled with. `provider_checkout` belongs to
 *  the moment of booking, which the reservation path has passed; `no_payment`
 *  is what `charging: 'free'` already says one question earlier.
 *
 *  Order = display order = test-account-baseline first (PO, 2026-08-19): bank
 *  transfer is what every tenant starts with, so it leads; the payment link
 *  depends on a configured Stripe account and comes second. */
export const RESERVATION_CHANNELS: TenantPaymentChannel[] = [
    'bank_transfer',
    'provider_link',
    'invoice',
];

/**
 * Which channels each flow admits — ADR-0016 **Amendment 6** (2026-09-02).
 * `RESERVATION_CHANNELS` above is the UNION (the vocabulary of a stored block);
 * this is what a tenant may actually choose, and the two sets are disjoint.
 *
 * Pre-pay takes the two channels that are payment instruments *in the
 * confirmation mail*. `invoice` is excluded because an invoice presupposes a
 * concluded contract, which pre-pay has not reached — until Amendment 6 the
 * combination was selectable and mailed the guest "Sie brauchen jetzt nichts
 * weiter zu tun" while the hold on their slot expired.
 *
 * Post-pay takes `invoice` alone: acceptance concludes the contract and the
 * invoice follows, from whoever `invoiceIssuer` names (ADR-0012). The other two
 * name payment details in an acceptance mail that, per ADR-0008's postpay
 * amendment, carries none.
 *
 * Mirror of `CHANNELS_BY_FLOW` in the backend rules, which rejects the rest on
 * write.
 */
export const CHANNELS_BY_FLOW: Record<TenantReservationFlow, TenantPaymentChannel[]> = {
    reserve_pay_first: ['bank_transfer', 'provider_link'],
    reserve_accept_postpay: ['invoice'],
};

/** The channels this flow admits, in display order. Empty for an unknown flow
 *  rather than the union — never hand out a menu for an unvalidated flow. */
export const channelsForFlow = (
    flow: TenantReservationFlow,
): TenantPaymentChannel[] => CHANNELS_BY_FLOW[flow] ?? [];

// --- Event types (docs/plans/tenant-configurable-event-types.md) -----------
//
// Mirror of the same block in `new-backend/admin/src/lib/serviceConfiguration.ts`,
// which is the copy pinned by tests. The STANDARD vocabulary is not here: it is
// localized and lives in `src/utils/index.ts` (`eventTypes`), which is why the
// read rule below takes it as an argument instead of owning it.

// Which use cases carry an event-type vocabulary is `space.eventTypes`
// (`utils/useCases.ts`, read by `categoryConfigRules.ts`); the backend refuses
// the key on any other use case, so the editor does not offer it there either.

/**
 * Use cases whose objects are ROOMS — a thing with parts, rather than a unit
 * inside a physical station — and so can be divided into segments (#1714).
 *
 * **The authority is `DIVISIBLE_USE_CASES` in
 * `new-backend/admin/src/lib/inventory/divideRoom.ts`**, which refuses a
 * division of anything else (ADR-0020: a rule a screen enforces is a
 * convention, not an invariant — and until 2026-09-20 this set existed ONLY
 * here, so the endpoint would divide a storage product for any direct caller).
 *
 * This copy exists because the browser has to decide whether to render a button
 * without a round trip. Both sides derive it from the same property of the
 * use-case description, `space.divisible` (`utils/useCases.ts`, pinned to
 * `shared/use-cases/use-case-description.json`), live use cases only, and
 * `new-backend/admin/src/lib/inventory/feContract.test.ts` fails if this file
 * stops deriving it that way.
 *
 * Why only these use cases is documented at the authority, in terms of what actually
 * breaks: a segment object gets no depot and no door, which is right for a room
 * and fatal for anything counted through a station's depots.
 *
 * It only decides whether the ACTION is offered. Whether a given room can
 * actually be divided is `assessDivisibility`, over live rows.
 */
export const DIVISIBLE_USE_CASES: ReadonlySet<string> = new Set<string>(
    liveUseCasesWhere((d) => d.space?.divisible === true),
);

/** An entry is a dropdown label, not a description. */
export const EVENT_TYPE_MAX_LENGTH = 40;

/** Upper bound on the list (ADR-0020): `tenant.config` must not grow without
 *  limit through an editor. */
export const EVENT_TYPES_MAX_ENTRIES = 30;

export interface EventTypesNormalization {
    /** The list to store, or `null` when `error` says why it cannot be. */
    types: string[] | null;
    /** The reason, phrased to read after a field path, or `null` when accepted. */
    error: string | null;
}

/**
 * The WRITE rule — what may be stored. Entries are trimmed and de-duplicated
 * case-insensitively (two entries differing only in case are indistinguishable
 * in a dropdown), and an empty result is an error rather than an empty list:
 * "use the standard list" is an ABSENT key, never a stored `[]`.
 *
 * Mirror of `normalizeEventTypes` in the backend rules, which rejects anything
 * else on write. Running it here is what keeps the form from offering a save
 * the endpoint then refuses.
 */
export const normalizeEventTypes = (input: unknown): EventTypesNormalization => {
    if (!Array.isArray(input)) {
        return { types: null, error: 'must be an array of event-type labels' };
    }
    if (input.length > EVENT_TYPES_MAX_ENTRIES) {
        return {
            types: null,
            error: `must hold at most ${EVENT_TYPES_MAX_ENTRIES} event types`,
        };
    }

    const types: string[] = [];
    const seen = new Set<string>();
    for (const raw of input) {
        if (typeof raw !== 'string') {
            return { types: null, error: 'entries must be strings' };
        }
        const label = raw.trim();
        if (!label) continue;
        if (label.length > EVENT_TYPE_MAX_LENGTH) {
            return {
                types: null,
                error: `entries must be at most ${EVENT_TYPE_MAX_LENGTH} characters`,
            };
        }
        const key = label.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        types.push(label);
    }

    if (types.length === 0) {
        return {
            types: null,
            error: 'must name at least one event type — omit it to use the standard list',
        };
    }
    return { types, error: null };
};

/**
 * The READ rule — the event types a surface should offer.
 *
 * Deliberately tolerant where the write rule is strict: a reader faces whatever
 * is already in the JSONB and must still render a usable dropdown. Anything
 * that is not a usable list (absent, not an array, all-blank) resolves to the
 * standard list, which is the documented default — and is what every legacy
 * tenant, having no stored block at all, always gets.
 *
 * ONE RULE, THREE DEPLOYABLES — mirrors `resolveEventTypes` in the backend
 * rules and the end-user app's `useEventTypeOptions`, plus `projectEventTypes`
 * in `new-backend/app` (which omits instead of falling back).
 */
export const resolveEventTypes = (
    configured: unknown,
    standard: readonly string[],
): string[] => {
    if (!Array.isArray(configured)) return [...standard];
    const usable = configured
        .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
        .map((value) => value.trim());
    return usable.length > 0 ? usable : [...standard];
};

/** What a choice needs to have been deposited in Account before it is offered. */
export type Prerequisite = 'stripe' | 'bank_details' | 'invoice_issuance';

export interface DepositedPrerequisites {
    /** The tenant's OWN Stripe credentials resolve. Shared credentials do not
     *  count — those sessions land on another tenant's account (#997). */
    stripeConnected: boolean;
    /** Bank details saved on the account (Account → Payments). */
    bankDetails: boolean;
    /** WHO issues the tenant's invoices (ADR-0012 WP0). `external` needs nothing
     *  more for the invoice channel; `kolula` prints the bank details on its
     *  invoice and so needs them (PO, 2026-10-19). */
    invoiceIssuer: TenantInvoiceIssuer | null;
}

/**
 * The template a use case's configuration starts from — "unsere
 * Voreinstellungen": chargeable, pre-pay by bank transfer, request rather than
 * direct booking (`unconfiguredDirectBooking`).
 *
 * A COPY. The backend owns the template and stores it on switch-on:
 * `PAYMENT_TEMPLATE` in `new-backend/admin/src/lib/serviceTemplate.ts`.
 * `paymentTemplateMirror.test.ts` reads that file and fails when the two
 * differ — change one, change the other.
 */
export const PAYMENT_TEMPLATE = {
    charging: 'chargeable',
    flow: 'reserve_pay_first',
    channel: 'bank_transfer',
} as const;

/**
 * The field the backend sets on an entry it stored from the template
 * (`TEMPLATE_MARKER` in `new-backend/admin/src/lib/serviceTemplate.ts`, pinned
 * by `paymentTemplateMirror.test.ts`). The tenant never saved such an entry,
 * so the screens keep showing it as the proposal it was — the two readers
 * below are the only ones; everything else treats it like any entry, because
 * it IS what bookings run on.
 */
export const SEEDED_ENTRY_MARKER = 'seededFromTemplate' as const;

/** A stored use-case entry the tenant saved — not one our template wrote. */
export const isSavedServiceEntry = (entry: TenantServiceConfig | null | undefined): boolean =>
    !!entry && entry[SEEDED_ENTRY_MARKER] !== true;

/**
 * Has the tenant saved a service configuration? `payments` used to answer it
 * by existing; since the template can create the block, a block holding
 * nothing but template entries does not count. Anything else in it — a saved
 * entry, the payment settings, a category — does, as before.
 */
export const hasSavedServicesConfig = (payments: TenantPayments | null | undefined): boolean => {
    if (!payments) return false;
    const { useCases, ...rest } = payments;
    if (Object.keys(rest).length > 0) return true;
    return Object.values(useCases ?? {}).some(isSavedServiceEntry);
};

/**
 * Whether the tenant's invoice issuance is configured — ADR-0012's
 * `invoiceIssuer` ∈ {`kolula`, `external`} (WP0, stored since 2026-08-19,
 * set in Account → Payments). Either mode satisfies the prerequisite —
 * `kolula` via its preliminary stage (invoice PDF from our system, number and
 * receipt on the booking; ADR-0012 amendment 2026-08-19). Absent = the
 * channel stays locked.
 */
export const isInvoiceIssuanceConfigured = (
    issuer: TenantInvoiceIssuer | null | undefined,
): boolean => issuer === 'external' || issuer === 'kolula';

/** The part of an effective entry the tenant-wide payment questions read. */
export type EffectivePaymentEntry = Pick<TenantServiceConfig, 'charging' | 'payment'>;

/** What the payment questions need from a category: where it sits, whether it
 *  is live, and what it overrides. */
export type CategoryOverrideSource = Pick<ProductCategory, 'useCase' | 'archivedAt' | 'config'>;

type StoredPayments = TenantPayments | null | undefined;

/**
 * Every arrangement this tenant can actually settle a booking under: each use
 * case's own entry, plus — for each live category that overrides charging or
 * payment — that category's override applied over its use case's entry
 * (ADR-0026 §U).
 *
 * The same field-by-field merge the booking backends run per booking
 * (`backend/src/core/lib/categoryOverrides.ts`, mirrored in
 * `new-backend/admin/src/lib/categoryOverrides.ts`): a field the category does
 * not name is the use case's, and `payment` is taken whole. Without the
 * categories, a category settled by bank transfer under an invoice-only use
 * case would never ask for the IBAN its confirmation mail prints.
 *
 * Archived categories are left out: they take no new bookings. A category
 * whose use case has no stored entry stands alone, and counts only once it
 * names its own charging — the backend reads anything less as unconfigured.
 */
export const effectivePaymentEntries = (
    payments: StoredPayments,
    categories: readonly CategoryOverrideSource[] = [],
): EffectivePaymentEntry[] => {
    const useCases = payments?.useCases ?? {};
    const fromCategories = categories
        .filter((category) => !category.archivedAt)
        .flatMap((category): EffectivePaymentEntry[] => {
            const own = category.config;
            if (!own || (own.charging === undefined && own.payment === undefined)) return [];
            const base = useCases[category.useCase.toLowerCase()];
            const charging = own.charging ?? base?.charging;
            if (!charging) return [];
            const payment = own.payment
                ? {
                    flow: own.payment.flow as TenantReservationFlow,
                    channels: own.payment.channels as TenantPaymentChannel[],
                }
                : base?.payment;
            return [{ charging, ...(payment ? { payment } : {}) }];
        });
    return [...Object.values(useCases), ...fromCategories];
};

/**
 * Does this tenant actually need an IBAN on file?
 *
 * Exactly when some chargeable offer is settled by a channel whose payment
 * request carries the bank details (PO, 2026-10-19 — whatever kolula sends that
 * asks for payment must contain a working way to pay):
 *
 *   - **bank transfer** — the reservation mail prints them, whoever issues the
 *     invoice afterwards;
 *   - **invoice issued by kolula** — kolula's invoice prints them.
 *
 * An invoice issued by the tenant's own system (`external`) needs no IBAN in
 * kolula: kolula sends no payment request, so the IBAN would appear nowhere.
 * A free offer needs nothing (ADR-0016 Amendment 4 ends the configuration at
 * `charging: 'free'`).
 *
 * Asked through `prerequisitesForChannel`, the same rule the channel choice in
 * Services is refused by, so the checklist and the form cannot disagree.
 *
 * An unconfigured tenant needs nothing: nothing is stored to read a channel
 * from, and guessing one would put a demand on screen that no decision of the
 * tenant's supports.
 */
export const bankDetailsRequiredForPayments = (
    payments: StoredPayments,
    categories: readonly CategoryOverrideSource[] = [],
    invoiceIssuer: TenantInvoiceIssuer | null | undefined = null,
): boolean =>
    effectivePaymentEntries(payments, categories).some(
        (config) => config?.charging !== 'free'
            && (config?.payment?.channels ?? []).some(
                (channel) => prerequisitesForChannel(channel, invoiceIssuer).includes('bank_details'),
            ),
    );

/**
 * May kolula produce the invoice document for this tenant's bookings?
 *
 * The "Rechnung erstellen" action on the booking detail pages asked only
 * whether invoice company data resolved, never who the tenant said issues
 * their invoices. So a tenant on `external` was offered our invoice PDF while
 * the Services form told them, one screen away, "Die Rechnung stellt und
 * versendet Dein eigenes System — kolula erstellt keine." One question, two
 * answers.
 *
 * Phrased as a refusal of the ONE mode that excludes it, never as a permission
 * granted by `kolula`: the issuer is an operational-tenant field, so a legacy
 * tenant has none at all, and gating on `=== 'kolula'` would take the invoice
 * PDF away from every tenant using it today — the setting is a statement about
 * who owns the invoice, not a feature switch anyone opted into. Unset keeps
 * today's behaviour, which is also the rule the Services wiring follows
 * (unconfigured = legacy behaviour, never a guess).
 */
export const mayIssueInvoiceFromKolula = (
    issuer: TenantInvoiceIssuer | null | undefined,
): boolean => issuer !== 'external';

export const prerequisiteForChannel = (
    channel: TenantPaymentChannel,
): Prerequisite | null => {
    switch (channel) {
        case 'provider_checkout':
        case 'provider_link':
            return 'stripe';
        case 'bank_transfer':
            return 'bank_details';
        case 'invoice':
            return 'invoice_issuance';
        default:
            return null;
    }
};

/**
 * EVERY prerequisite a channel needs, in the order a tenant deposits them —
 * an invoice issued by kolula needs the issuer AND the bank details it prints.
 *
 * ONE RULE, TWO COPIES — mirrors `prerequisitesForChannel` in `@kolula/shared`
 * (`shared/src/bookingPageActivation.ts`, re-exported by the new backend's
 * `serviceConfiguration.ts`), which is authoritative; pinned by
 * `bookingPageActivationParity.test.ts`.
 */
export const prerequisitesForChannel = (
    channel: TenantPaymentChannel,
    invoiceIssuer: TenantInvoiceIssuer | null | undefined,
): Prerequisite[] => {
    const primary = prerequisiteForChannel(channel);
    if (!primary) return [];
    if (channel === 'invoice' && invoiceIssuer === 'kolula') return [primary, 'bank_details'];
    return [primary];
};

export const isPrerequisiteMet = (
    prerequisite: Prerequisite,
    deposited: DepositedPrerequisites,
): boolean => {
    switch (prerequisite) {
        case 'stripe':
            return deposited.stripeConnected;
        case 'bank_details':
            return deposited.bankDetails;
        case 'invoice_issuance':
            return isInvoiceIssuanceConfigured(deposited.invoiceIssuer);
    }
};

/** The first unmet prerequisite blocking a channel, or `null` when selectable. */
export const unmetPrerequisiteForChannel = (
    channel: TenantPaymentChannel,
    deposited: DepositedPrerequisites,
): Prerequisite | null =>
    prerequisitesForChannel(channel, deposited.invoiceIssuer)
        .find((prerequisite) => !isPrerequisiteMet(prerequisite, deposited)) ?? null;

/**
 * The translation key of the hint under a channel whose prerequisite is
 * missing. Specific to the channel where the same prerequisite means something
 * different: bank details for a transfer go out in the mail, bank details for
 * a kolula invoice are printed on it.
 */
export const prerequisiteHintKey = (
    prerequisite: Prerequisite,
    channel?: TenantPaymentChannel,
): string =>
    (channel === 'invoice' && prerequisite === 'bank_details'
        ? 'services.configuration.prerequisite_bank_details_invoice'
        : `services.configuration.prerequisite_${prerequisite}`);

/**
 * Whether a use case the tenant never configured books directly — the value
 * the Services form seeds an unconfigured use case (and the proposed standard
 * category) with.
 *
 * Read from the use-case description's `space.directBookingDefault`, the one
 * basis every surface answers this from (PO, 2026-10-19; ADR-0026 amendment
 * 2026-10-19): `false`, a reservation request, for every space use case. The
 * end-user app asks the same property (`useDirectBookingFlow.ts`), and both
 * `paymentArrangement.ts` copies read an absent value as not direct. A use
 * case without space properties (stations, handover) keeps the form's
 * previous seed, `false`.
 */
export const unconfiguredDirectBooking = (useCase: string): boolean =>
    spaceOf(useCase)?.directBookingDefault ?? false;

/**
 * The unmet prerequisite blocking direct booking, or `null` when it may be
 * switched on.
 *
 * Direct booking hands the slot out at the moment of booking, so anything that
 * costs money has to be collected then — an interactive checkout, i.e. the
 * tenant's own Stripe. `mixed` counts as chargeable: a direct booking under it
 * can land on the chargeable half. A free offer needs nothing, which is why
 * charging is asked before payment — Segmenta's free lockers reach direct
 * booking without a payment provider at all.
 */
export const unmetPrerequisiteForDirectBooking = (
    charging: TenantChargingMode,
    deposited: DepositedPrerequisites,
): Prerequisite | null => {
    if (charging === 'free') return null;
    return deposited.stripeConnected ? null : 'stripe';
};

/**
 * Whether every way the tenant asks to be paid actually works.
 *
 * Bank details are NOT a universal requirement. ADR-0016 Amendment 4 makes them
 * a prerequisite of a **channel**, and ends the configuration outright for
 * `charging: 'free'`. A tenant whose offers are free — KGSt-Forum's storage, for
 * one — was once unable to activate its booking page without an IBAN, because
 * this gate asked for one unconditionally.
 *
 * The rule (PO, 2026-09-07, sharpened 2026-10-19): the moment a use case is set
 * kostenpflichtig it needs a channel, and EVERY channel it offers must work —
 * whatever kolula sends that asks for payment must contain a working way to
 * pay. A bank transfer needs bank details; an invoice needs an issuer, and when
 * kolula issues it, bank details as well; a provider channel needs Stripe.
 *
 * What is checked here, and what is not: the *choice* of channel is validated in
 * Services, where `unmetPrerequisiteForChannel` refuses a channel whose
 * prerequisite is not deposited. This gate re-checks what the account payload
 * can see — bank details and the invoice issuer — and takes Stripe as
 * configured, because the Stripe connection state is not on this payload and a
 * second source of truth for it is worse than the gap. Re-checking still
 * matters: the issuer can change after an offer was saved.
 *
 * COPY of `hasPaymentRouteForChargeableUseCases` in `@kolula/shared`, which the
 * API runs when a booking page goes live (PO, 2026-10-30); pinned, with
 * `effectivePaymentEntries` and `canCreateBookings`, by
 * `bookingPageActivationParity.test.ts`.
 */
export const hasPaymentRouteForChargeableUseCases = (
    payments: StoredPayments,
    deposited: { bankDetails: boolean, invoiceIssuer: TenantInvoiceIssuer | null | undefined },
    categories: readonly CategoryOverrideSource[] = [],
): { required: boolean, satisfied: boolean } => {
    // Category overrides count too (ADR-0026 §U): a category set chargeable
    // under a free use case needs a way to be paid like any other offer.
    const entries = effectivePaymentEntries(payments, categories);
    const chargeable = entries.filter((config) => config?.charging !== 'free');
    if (chargeable.length === 0) return { required: false, satisfied: true };

    // Stripe taken as configured — see above.
    const asDeposited: DepositedPrerequisites = {
        stripeConnected: true,
        bankDetails: deposited.bankDetails,
        invoiceIssuer: deposited.invoiceIssuer ?? null,
    };
    const satisfied = chargeable.every((config) => {
        const channels = config.payment?.channels ?? [];
        if (channels.length === 0) return false;
        // Every channel offered must work (PO, 2026-10-19): a working payment
        // link does not make a transfer without an IBAN payable.
        return channels.every((channel) => !unmetPrerequisiteForChannel(channel, asDeposited));
    });

    return { required: true, satisfied };
};

/**
 * Are a group's prices fixed at 0,00 because a use case it serves is free?
 *
 * ── Why this is "prices locked" and NOT "editor locked" ────────────────────
 *
 * "Free" means every row prices at zero (ADR-0018 rule 4). It has never meant
 * that the tenant may not decide **which durations are bookable** — and the
 * backend has always agreed: `freeUseCaseBlockingWrite`
 * (`new-backend/admin/src/controllers/priceCategoryController.ts`) admits any
 * write whose rows are all zero, commented *"Zero rows are what 'free' means
 * in the data — always admissible"*, one line below *"the UI lock alone is not
 * a rule"*.
 *
 * The pricing section used to hide the editor entirely while a use case was
 * free, which was stricter than the rule it mirrored. The consequence was
 * concrete: a free storage tenant keeps only the generated 1440-minute anchor
 * row (`lib/pricing/freeRows.ts`), the durations offered in the booking flow
 * are exactly the distinct `duration_in_minutes` of those rows, and so the
 * customer could book nothing but "1 Tag" with no way for anyone to change it
 * (PO, KGSt-Forum 2026-09-11).
 *
 * ── Why it asks about the GROUP, not the rendered service ──────────────────
 *
 * The backend derives the category's use cases from `Group.compartment_types`
 * and refuses a non-zero write if ANY of them is free. Asking only about the
 * service currently on screen would let the dialog offer a price field that the
 * save then rejects with a 409. `compartment_types` is the overloaded column
 * that also carries locker sizes ("L", "M"); a size has no charging entry and
 * is therefore never free, which is the correct answer for it.
 *
 * ONE RULE, TWO COPIES — they cannot import each other (separate deploys):
 * this one, and `freeUseCaseBlockingWrite` in the new backend, which is
 * authoritative. Change one and change the other.
 */
export const isPriceLockedForGroup = (
    compartmentTypes: readonly string[] | null | undefined,
    payments: TenantPayments | null | undefined,
): boolean => {
    /*
     * EVERY, not SOME — and the difference is data loss.
     *
     * The first version locked when ANY use case of the group was free, to
     * mirror the backend's refusal. But locking also makes the dialog WRITE
     * `price: 0` for every editable row of the whole price category. On a group
     * serving `['rental','storage']` with storage free and rental chargeable,
     * an operator opening the editor from the rental section would have seen 0,
     * pressed save, and wiped the rental prices. Before this change the editor
     * simply did not open there.
     *
     * With EVERY, a mixed group is not locked: the editor shows the real prices
     * and the backend's 409 decides, exactly as before. Only a group whose use
     * cases are ALL free — the KGSt case — gets the zero-pinned editor, where
     * writing 0 is what the rows already are.
     *
     * A locker size in `compartment_types` ("L", "M") has no charging entry, so
     * it is not free and correctly prevents the lock.
     */
    const types = (compartmentTypes ?? []).filter(
        (type) => typeof type === 'string' && type.trim().length > 0,
    );
    if (types.length === 0) return false;
    return types.every(
        (type) => payments?.useCases?.[type.toLowerCase()]?.charging === 'free',
    );
};

/**
 * Are a ROOM PRODUCT's prices fixed at 0,00 because it is offered free?
 *
 * Free by its product category's own charging, else its use case's — the same
 * merge the backend's free switch and price-write lock run
 * (`new-backend/admin/src/lib/pricing/freePriceLists.ts`). Without the
 * category, the room price dialog opened editable on a free category's product
 * and the save was refused with a 409; with it, the dialog shows the zero rows
 * the category's save already wrote (ADR-0018 rule 4, ADR-0026 §W).
 *
 * The data-loss concern `isPriceLockedForGroup` guards against does not arise
 * here: a category is only stored free when none of its lists is shared with a
 * chargeable offer — the backend refuses that save.
 */
export const isPriceLockedForProduct = (
    useCase: string,
    productCategoryId: string | null,
    categories: readonly Pick<ProductCategory, 'id' | 'archivedAt' | 'config'>[],
    payments: StoredPayments,
): boolean => {
    const category = productCategoryId
        ? categories.find((c) => c.id === productCategoryId && !c.archivedAt)
        : undefined;
    const charging = category?.config?.charging
        ?? payments?.useCases?.[useCase.toLowerCase()]?.charging;
    return charging === 'free';
};

/**
 * The event-type part of the comparison in `renderService`, normalized so that a blank row
 * the tenant added and has not filled in yet does not by itself mark the form
 * unsaved. An UNUSABLE list still compares by its raw rows — the tenant is
 * mid-edit and the badge should say so.
 */
const renderEventTypes = (types: string[] | undefined): string => {
    if (!types) return 'standard';
    return (normalizeEventTypes(types).types ?? types).join('+');
};

/** Order-independent rendering, so comparing the form to what is stored does
 *  not depend on JSONB key order. It decides "unsaved changes" and whether
 *  Save is enabled — a field missing here can be edited but never saved alone. */
export const renderService = (s: TenantServiceConfig): string =>
    [
        s.directBooking ? 'direct' : 'reserve-only',
        s.charging,
        s.payment ? `${s.payment.flow}:${[...s.payment.channels].sort((a, b) => a.localeCompare(b)).join('+')}` : '-',
        renderEventTypes(s.eventTypes),
        s.bookingWindow
            ? `${s.bookingWindow.leadMinutes}/${s.bookingWindow.horizonMonths}`
              + `/${s.bookingWindow.followUpMinutes}`
              // Absent = one night, so a window stored before the field existed
              // does not read as changed.
              + `/${s.bookingWindow.minNights ?? 1}`
            : '-',
    ].join(',');
